import { AsyncPipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';

import { DownloadTemplateButtonComponent } from '@features/download-template-button/download-template-button.component';

import { FileManagerComponent } from '@shared/components/file-manager/file-manager.component';
import { File } from '@shared/components/file-manager/state/file.model';
import { IconComponent } from '@shared/components/icon/icon.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { FileMetadata } from '@shared/services/api.service';
import { EntityType, EventType, TemplateType } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { Utils } from '@shared/utils/utils';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DownloadTemplateButtonComponent, FileManagerComponent, AsyncPipe, IconComponent],
  selector: 'aux-add-bulk-sites-dialog',
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
  templateUrl: './add-bulk-sites-dialog.component.html',
})
export class AddBulkSitesDialogComponent implements AfterViewInit {
  organizationQuery = inject(OrganizationQuery);
  ref = inject(CustomOverlayRef);

  private eventService = inject(EventService);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);

  errorMessage = signal('');

  @ViewChild('bulkFileManager') bulkFileManager: FileManagerComponent | undefined;
  bulkFiles$: Observable<File[]> | undefined;
  countries: { label: string; value: string }[];
  loading$ = new BehaviorSubject(false);
  path = '';
  siteTemplateType = TemplateType.BULK_SITE_TEMPLATE;

  ngAfterViewInit(): void {
    if (this.bulkFileManager) {
      this.bulkFiles$ = this.bulkFileManager.fileQuery.selectAll();
    }
  }

  constructor() {
    this.countries = Utils.getCountriesForSelectOptions();
  }

  getBulkFilePath() {
    const trialId = this.mainQuery.getValue().trialKey;

    return `trials/${trialId}/bulk-site-templates/`;
  }

  getMetadata(): FileMetadata {
    return { onboarding: 'true' };
  }

  async onBulkUpload() {
    this.errorMessage.set('');

    if (this.bulkFileManager && !this.loading$.getValue()) {
      const files = this.bulkFileManager.fileQuery.getAll();

      if (!files.length) {
        this.errorMessage.set(MessagesConstants.FILE.NEED_UPLOAD_FILE);

        return;
      }

      if (files.length > 1) {
        this.errorMessage.set(MessagesConstants.FILE.MAX_ONE_FILE);

        return;
      }

      this.loading$.next(true);

      const file = files[0];
      const bucket_key = `${this.getBulkFilePath()}${file.bucket_key}`;

      this.bulkFileManager.fileStore.update(file.id, {
        ...file,
        bucket_key,
      });

      const fileSuccess = await this.bulkFileManager.fileService.uploadFiles({});

      if (fileSuccess) {
        const { errors, success } = await firstValueFrom(
          this.eventService.processEvent$({
            bucket_key: `public/${bucket_key}`,
            entity_id: this.mainQuery.getValue().trialKey,
            entity_type: EntityType.SITE,
            type: EventType.BULK_SITE_TEMPLATE_UPLOADED,
          })
        );

        if (success) {
          this.overlayService.success();
        } else {
          this.overlayService.error(errors, undefined, true);
        }

        this.ref.close(true);
      }
    }

    this.loading$.next(false);
  }

  removeBulkFile(file: File) {
    if (this.bulkFileManager) {
      void this.bulkFileManager.removeFile(file);
    }
  }
}
